import React, { useState, useEffect, useRef } from "react";
import { IQuantitySelector } from "types/gaiaComponents/quantitySelector";
import { KEY } from "./constants";
import { MinusIcon, PlusIcon } from "lucide-react";

export const QuantitySelector = (props: IQuantitySelector) => {
    const {
        value,
        id,
        ariaLabel,
        min,
        max,
        step = 1,
        precision = 0,
        disabled = false,
        strict = false,
        tabIndex,
        onChange,
        onBlur,
        dataTestId,
    } = props;

    const [inputValue, setInputValue] = useState<string>(value.toFixed(precision));
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        setInputValue(value.toFixed(precision));
    }, [value, precision]);

    const toFixed = (num: number): number => {
        return parseFloat(num.toFixed(precision));
    };

    const isAtMin = (): boolean => {
        const current = parseFloat(inputValue);
        return min !== undefined && !isNaN(current) && current <= min;
    };

    const isAtMax = (): boolean => {
        const current = parseFloat(inputValue);
        return max !== undefined && !isNaN(current) && current >= max;
    };

    const handleDecrease = () => {
        const current = parseFloat(inputValue);
        if (isNaN(current)) return;
        const next = toFixed(current - step);
        const newValue = min !== undefined ? Math.max(next, min) : next;
        setInputValue(newValue.toFixed(precision));
        fireChange(newValue.toFixed(precision), inputRef.current);
    };

    const handleIncrease = () => {
        const current = parseFloat(inputValue);
        if (isNaN(current)) return;
        const next = toFixed(current + step);
        const newValue = max !== undefined ? Math.min(next, max) : next;
        setInputValue(newValue.toFixed(precision));
        fireChange(newValue.toFixed(precision), inputRef.current);
    };

    const fireChange = (strValue: string, inputElement: HTMLInputElement | null) => {
        if (!onChange || !inputElement) return;

        // Update the input element's value
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
            window.HTMLInputElement.prototype,
            'value'
        )?.set;

        if (nativeInputValueSetter) {
            nativeInputValueSetter.call(inputElement, strValue);
        }

        // Create a mock change event object with both target and currentTarget
        // We don't spread SyntheticEvent - instead we create a plain object
        // with the properties that consumers expect
        const mockEvent = {
            target: inputElement,
            currentTarget: inputElement,
            bubbles: true,
            cancelable: false,
            defaultPrevented: false,
            eventPhase: 3,
            isTrusted: false,
            preventDefault: () => {},
            stopPropagation: () => {},
            isPropagationStopped: () => false,
            persist: () => {},
        } as React.ChangeEvent<HTMLInputElement>;

        onChange(mockEvent);
    };

    const isValidNumericInput = (input: string): boolean => {
        if (input === "") return true;

        let hasDecimal = false;
        for (let i = 0; i < input.length; i++) {
            const char = input[i];
            if (char === '-' && i !== 0) return false; // minus only at start
            if (char === '.') {
                if (hasDecimal) return false; // only one decimal
                hasDecimal = true;
            } else if (char !== '-' && (char < '0' || char > '9')) {
                return false; // invalid character
            }
        }
        return true;
    };

    const isWithinBounds = (num: number): boolean => {
        return !((min !== undefined && num < min) || (max !== undefined && num > max));
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const raw = event.target.value;

        if (!isValidNumericInput(raw)) {
            return;
        }

        // Allow clearing the field, but avoid storing other non-numeric intermediates (e.g. "-" or ".")
        if (raw === "") {
            setInputValue(raw);
            return;
        }
        const num = parseFloat(raw);
        if (isNaN(num)) {
            // Do not update state with values that are not valid numbers for a type="number" input
            return;
        }
        if (strict && !isWithinBounds(num)) {
            return;
        }
        setInputValue(raw);
        if (onChange) {
            onChange(event);
        }
    };

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        let num = parseFloat(inputValue);

        if (isNaN(num)) {
            num = value;
        }

        if (min !== undefined && num < min) {
            num = min;
        }
        if (max !== undefined && num > max) {
            num = max;
        }

        const clamped = num.toFixed(precision);
        const needsClamping = clamped !== inputValue;

        setInputValue(clamped);

        // If the value was clamped, notify via onChange with the new value
        if (needsClamping && onChange) {
            fireChange(clamped, inputRef.current);
        }

        // Call onBlur with the original event (no spreading)
        if (onBlur) {
            onBlur(event);
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === KEY.ARROW_UP) {
            event.preventDefault();
            if (!isAtMax()) {
                handleIncrease();
            }
        } else if (event.key === KEY.ARROW_DOWN) {
            event.preventDefault();
            if (!isAtMin()) {
                handleDecrease();
            }
        }
    };

    return (
        <div className="ga-input ga-input--quantity-selector">
            <input ref={inputRef}
                   type="text"
                   inputMode="decimal"
                   id={id}
                   aria-label={ariaLabel}
                   value={inputValue}
                   disabled={disabled}
                   tabIndex={tabIndex}
                   data-testid={dataTestId}
                   onChange={handleInputChange}
                   onBlur={handleBlur}
                   onKeyDown={handleKeyDown} />
            <button type="button" className="ga-input__quantity-button"
                    onClick={handleDecrease}
                    disabled={disabled || isAtMin()}
                    tabIndex={tabIndex}
                    aria-label="Decrease">
                <MinusIcon size={16} />
            </button>
            <div className="ga-input__quantity-separator" />
            <button className="ga-input__quantity-button"
                    type="button"
                    onClick={handleIncrease}
                    disabled={disabled || isAtMax()}
                    tabIndex={tabIndex}
                    aria-label="Increase">
                <PlusIcon size={16} />
            </button>
    </div>
    );
};
